import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import type { ApiPermission } from '@/api/schema/data-contracts';

import FieldErrors from './FieldErrors';
import { UserAutocompleteField } from './UserAutocompleteField';

export type UserAutocompleteFormFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  onChange?: (userIds: string | string[] | null) => void;
  onBlur?: () => void;
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  isDisabled?: boolean;
  isMulti?: boolean;
  permissions?: ApiPermission[];
  'data-testid'?: string;
};

export const UserAutocompleteFormField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  onChange,
  onBlur,
  fieldPath,
  control,
  rules,
  isDisabled,
  isMulti,
  permissions,
  'data-testid': dataTestId,
}: UserAutocompleteFormFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { name, onBlur: innerOnBlur, onChange: innerOnChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    onBlur?.();
  }, [onBlur, innerOnBlur]);

  const composedOnChange = useCallback(
    (userIds: string | string[] | null) => {
      innerOnChange(userIds);
      onChange?.(userIds);
    },
    [onChange, innerOnChange],
  );

  return (
    <div>
      <UserAutocompleteField
        userIds={value}
        onChange={composedOnChange}
        ref={ref}
        name={name}
        onBlur={composedOnBlur}
        isDisabled={isDisabled}
        isMulti={isMulti}
        permissions={permissions}
        data-testid={dataTestId}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
